import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { HiOutlineClipboardCopy } from "react-icons/hi";
import { subscribeQuizMaster } from "./quizApiService";
import { getTeacherSession, updateTeacherSession } from "./quizSession";

export default function QuizTeacherLobby() {
  const { t } = useTranslation();
  const { sessionId } = useParams(); // shortId
  const navigate = useNavigate();

  const session = useRef(getTeacherSession(sessionId)).current;

  const joinUrl = `${window.location.origin}/quiz/join/${sessionId}`;

  const [joinedStudents, setJoinedStudents] = useState([]);
  const [copied, setCopied] = useState(false);

  // Subscribe to the teacher (master) event stream for participant joins.
  useEffect(() => {
    if (!session?.uuidQuiz) return undefined;

    const unsubscribe = subscribeQuizMaster(session.uuidQuiz, {
      onEvent: (event) => {
        if (event.eventType !== "PARTICIPANT_JOINED_EVENT") return;
        const payload = event.payload || {};
        const uuid = payload.uuidParticipant || payload.uuidParticipan;
        if (!uuid) return;

        setJoinedStudents((prev) => {
          if (prev.some((s) => s.uuidParticipant === uuid)) return prev;
          const joinedAt = new Date(event.timestamp || Date.now()).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
          const next = [...prev, { uuidParticipant: uuid, name: payload.name || "—", joinedAt }];
          updateTeacherSession(sessionId, { participants: next });
          return next;
        });
      },
    });

    return unsubscribe;
  }, [session, sessionId]);

  const handleStart = () => {
    navigate(`/quiz/session/${sessionId}/teacher/live`);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!session?.uuidQuiz) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="text-center">
          <h2 className="text-xl font-bold text-black mb-2">
            {t("quiz.sessionNotFound") || "Quiz session not found"}
          </h2>
          <p className="text-lightgray mb-4">
            {t("quiz.sessionNotFoundHint") || "Please create the quiz again."}
          </p>
          <button
            onClick={() => navigate("/quiz")}
            className="primary-btn !rounded-full h-11 !py-0 flex items-center justify-center px-7 mx-auto"
          >
            {t("quiz.backToHome")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full">
      <div className="max-w-lg mx-auto p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/quiz")}
            className="border border-blue-50 cursor-pointer rounded-full flex justify-center items-center size-10 bg-white shadow-sm"
          >
            <FaLongArrowAltLeft />
          </button>
          <h1 className="text-xl font-semibold text-black">
            {t("quiz.quizLobby")}
          </h1>
        </div>

        {/* QR Code Card */}
        <div className="rounded-3xl bg-white p-8 shadow-sm border border-blue-50 mb-6">
          <div className="flex justify-center mb-5">
            <div className="p-4 bg-white rounded-2xl shadow-sm border border-blue-50">
              <QRCodeSVG
                value={joinUrl}
                size={200}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#1E4AD9"
              />
            </div>
          </div>

          {/* Join code */}
          <div className="text-center mb-4">
            <p className="text-sm text-lightgray mb-1">{t("quiz.joinCode") || "Join code"}</p>
            <p className="text-4xl font-black tracking-[0.3em] text-darkblue">{sessionId}</p>
          </div>

          <p className="medium-text text-center text-black mb-4">
            {t("quiz.scanToJoin")}
          </p>

          {/* Copyable URL */}
          <button
            onClick={handleCopy}
            className="w-full flex items-center justify-between gap-2 bg-bluelight rounded-xl p-3 cursor-pointer hover:bg-blue-100 transition-colors"
          >
            <span className="text-sm text-darkblue truncate flex-1 text-left font-medium">
              {joinUrl}
            </span>
            <span className="flex items-center gap-1 text-sm font-bold text-darkblue flex-shrink-0 bg-white px-3 py-1 rounded-lg shadow-sm">
              <HiOutlineClipboardCopy />
              {copied ? "✓" : t("quiz.copy") || "Copy"}
            </span>
          </button>
        </div>

        {/* Separator */}
        <div className="my-6">
          <div className="h-px bg-blue-100 w-full"></div>
        </div>

        {/* Students joined */}
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-blue-50 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="medium-text text-black flex items-center gap-2">
              {t("quiz.studentsJoined")}
            </h2>
            <span className="bg-darkblue text-white rounded-full px-3 py-1 text-sm font-semibold shadow-sm">
              {joinedStudents.length}
            </span>
          </div>

          {joinedStudents.length === 0 ? (
            <p className="text-sm text-lightgray text-center py-6 animate-pulse">
              {t("quiz.waitingForStudents")}
            </p>
          ) : (
            <div className="space-y-3">
              {joinedStudents.map((student) => {
                const colors = ["bg-blue-500", "bg-purple-500", "bg-pink-500", "bg-indigo-500", "bg-teal-500"];
                const colorClass = colors[student.name.length % colors.length];

                return (
                  <div
                    key={student.uuidParticipant}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-blue-50 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full ${colorClass} text-white flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-sm`}>
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="regular-text font-medium text-black flex-1">
                      {student.name}
                    </span>
                    <span className="text-xs font-medium text-lightgray bg-gray-50 px-2 py-1 rounded-md">
                      {student.joinedAt}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Start Quiz */}
        <button
          onClick={handleStart}
          disabled={joinedStudents.length === 0}
          className={`primary-btn w-full !rounded-full h-12 !py-0 flex items-center justify-center text-base font-semibold ${
            joinedStudents.length === 0 ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {t("quiz.startQuiz")}
        </button>
      </div>
    </div>
  );
}
